import React, { useCallback, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRetweet } from '@soundxyz/font-awesome-new/pro-regular-svg-icons/faArrowsRetweet.js';
import type { PostModalSource } from '@soundxyz/utils/analytics';
import { DropdownMenuItem, MenuItemLabel } from '@components/basics/Dropdowns/GlobalDropdown';
import { FeedPostModal } from '@components/modals/FeedPostModal';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { ModalContext, ModalType } from '@context/modal';
import {
  FeedPostModalMainSoundFragmentDoc,
  type FragmentType,
  getFragment,
} from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { DataTestId } from '@utils/test';

export const PostReleaseMenuItem = ({
  releaseFrag,
  source,
}: {
  releaseFrag: FragmentType<FeedPostModalMainSoundFragmentDoc>;
  source: PostModalSource;
}) => {
  const { openModal } = ModalContext.useContainer();
  const { isAuthenticatedWithAPI } = useAuth();
  const release = getFragment(FeedPostModalMainSoundFragmentDoc, releaseFrag);

  const handleClick = useCallback(() => {
    if (!isAuthenticatedWithAPI) {
      triggerLogin();
    } else {
      trackClickEvent({
        event: 'Create Post Clicked',
        properties: {
          sourceComponent: source,
          associationId: release.id,
          associationType: 'Release',
        },
      });
      openModal(ModalType.FEED_POST, {
        body: (
          <FeedPostModal
            key={release.id}
            sourceLocation="Song Card"
            preselectedResource={{
              type: 'release',
              release,
            }}
            channelId={null}
          />
        ),
      });
    }
  }, [isAuthenticatedWithAPI, openModal, release, source]);

  const menuItem = useMemo(
    () => (
      <DropdownMenuItem onClick={handleClick} data-testid={DataTestId.FEED_POST_MENU_ITEM_DROPDOWN}>
        <FontAwesomeIcon icon={faArrowsRetweet} size="lg" />
        <MenuItemLabel>Repost</MenuItemLabel>
      </DropdownMenuItem>
    ),
    [handleClick],
  );

  return menuItem;
};
